import React, { FC, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { get, orderBy } from 'lodash';

import { getTutorials, setActiveTutorial } from '../../../store/tutorials';
import { tutorialConstants } from '../../../utils/constants';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

import CreateFacility from '../../forms/createFacility';
import CreateOrganization from '../../forms/createOrganization';
import Elevation from '../../themed/instruction-modals/elevation';
import Header from '../../blocks/header';
import Input from '../../themed/input/input';
import Instruction from '../../themed/instruction-modals/instruction';
import Modal from '../../themed/modal';
import NoItems from '../../blocks/no-items';
import Pagination from '../../themed/pagination';
import Select from '../../themed/select/select';
import Status from '../../blocks/status';
import SyncTime from '../../blocks/sync-time';
import Table from '../../common/table';

import withRouter from '../../hoc/withRouter';
import withOrganizations from '../../hoc/withOrganizations';
import withFacilities from '../../hoc/withFacilities';

import classNames from './style.module.scss';
import routes from '../../../utils/routes';

const Organizations: FC = (props: any) => {
	const [searchQuery, setSearchQuery] = useState<string>('');
	const [searchKey, setSearchKey] = useState<string>('');
	const [sortColumn, setSortColumn] = useState(initialSortColumn);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(12);
	const [createOrganization, setCreateOrganization] = useState<boolean>(false);
	const [createFacility, setCreateFacility] = useState<boolean>(false);

	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const tutorials = useAppSelector(getTutorials);
	const organizations: models.Organization[] = props.organizations;

	const filteredSearch = useMemo((): models.Organization[] => {
		const searchable: string = searchQuery.toLowerCase();
		const key: string = searchKey || 'organizationName';

		return searchable
			? organizations.filter((o) => get(o, key).toLowerCase().indexOf(searchable) !== -1)
			: organizations;
	}, [searchQuery, searchKey, organizations]);

	const paginatedData = useMemo(() => {
		let filtered: models.Organization[] = filteredSearch;

		const sorted = orderBy(filtered, [sortColumn.path], [sortColumn.order]);
		const paginated = utils.paginate(sorted, currentPage, pageSize);

		return { totalCount: filtered.length, paginated };
	}, [filteredSearch, sortColumn, currentPage, pageSize]);

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.target.value);
	};
	
	const handlepageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const pageSize: number = +e.target.value;
		const actualPageSize: number = pageSize === 0 ? 1 : pageSize;

		setPageSize(actualPageSize);
	};

	const handleSearchKeyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		setSearchKey(e.target.value);
	};

	const handleViewNextTutorial = () => {
		dispatch(setActiveTutorial(tutorialConstants.PROFILE_SETTINGS));
		navigate(routes.profileSettings.fullPath);
	};

	const toggleCreateOrganization = (): void => {
		setCreateOrganization((p: boolean) => !p);
	};

	const toggleCreateFacility = (): void => {
		setCreateFacility((p: boolean) => !p);
	};

	const isActive = tutorials.activeTutorial === tutorialConstants.MANAGE_ORGANIZATIONS;

	return (
		<React.Fragment>
			<Instruction
				isVisible={isActive}
				onProceed={handleViewNextTutorial}
				title="Manage Organizations!"
				message="Here, you can setup client accounts and facilities!"
			/>

			<Modal isVisible={createOrganization} onClose={toggleCreateOrganization}>
				<CreateOrganization />
			</Modal>

			<Modal isVisible={createFacility} onClose={toggleCreateFacility}>
				<CreateFacility />
			</Modal>

			<Header subtitle="Showing Registered Organizations" />

			<div className={classNames.users}>
				<Elevation isElevated={isActive}>
					<div className="container-fluid overflow-hidden">
						<div className="row">
							<div className={classNames.usersOverviewItem + ' col'}>
								<div className={classNames.usersOverviewItemIcon}>
									<i className={'bi bi-' + routes.organizations.icon}></i>
								</div>

								<div className={classNames.usersOverviewItemBody}>
									<h3>Add New Organization</h3>
									<p>Register a New Organization</p>
								</div>

								<button onClick={toggleCreateOrganization}>
									<i className="bi bi-chevron-right"></i>
								</button>
							</div>

							<div className={classNames.usersOverviewItem + ' col'}>
								<div className={classNames.usersOverviewItemIcon}>
									<i className="bi bi-cone-striped"></i>
								</div>

								<div className={classNames.usersOverviewItemBody}>
									<h3>Add New Facility</h3>
									<p>Register a New Facility</p>
								</div>

								<button onClick={toggleCreateFacility}>
									<i className="bi bi-chevron-right"></i>
								</button>
							</div>
						</div>
					</div>
				</Elevation>

				<div className="divider my-4"></div>

				<div className={classNames.usersHeader}>
					<div className="d-flex justify-content-between">
						<div>
							<h4>{utils.formatDigits(organizations.length)} </h4>
							<p>Registered Organization(s)</p>
						</div>

						<div className="d-flex justify-content-center">
							<div className={classNames.usersHeaderOverview}>
								<i className="bi bi-cone-striped"></i>
								<span>{props.facilities.length} Total Facilities</span>
							</div>
						</div>
					</div>
				</div>

				<div className="divider my-4"></div>

				<SyncTime lastSynced={props.organizationsLastUpdated} />

				<div className="divider my-4"></div>

				<div className={classNames.usersBody}>
					<div className="d-flex justify-content-between">
						<div>
							<p className={classNames.usersBodyTitle}>All Organizations</p>
						</div>

						<div className="d-flex justify-content-center">
							<div className={classNames.filterIcon}>
								<i className="bi bi-filter-left"></i>
							</div>

							<div className="mx-3">
								<Select
									name="page-size"
									placeholder="Search By"
									options={filterOptions}
									value={searchKey}
									onChange={handleSearchKeyChange}
								/>
							</div>

							<div>
								<Input
									name="search"
									value={searchQuery}
									onChange={handleSearchChange}
									placeholder="Enter to search"
								/>
							</div>
						</div>
					</div>

					<div className={classNames.usersBodyItems}>
						<div className="bg-white p-3 border">
							<Table
								columns={columns}
								data={paginatedData.paginated}
								sortColumn={sortColumn}
								onSort={setSortColumn}
								isFetching={props.organizationsLoading}
							/>
						</div>

						<NoItems
							isVisible={organizations.length === 0}
							onRefresh={props.refreshOrganizations}
						/>
					</div>

					<div className="d-flex justify-content-between align-items-center mt-3">
						<Select
							name="page-size"
							placeholder={'Showing (' + pageSize + 'rows)'}
							options={utils.transformPageRange(20)}
							value={pageSize}
							onChange={handlepageSizeChange}
						/>

						<Pagination
							itemsCount={paginatedData.totalCount}
							pageSize={pageSize}
							currentPage={currentPage}
							onPageChange={(page: number) => setCurrentPage(page)}
						/>
					</div>
				</div>
			</div>
		</React.Fragment>
	);
};

const filterOptions: models.SelectOption[] = [
	{ id: 'organisationName', name: 'Organization Name' },
	{ id: 'organisationWebsite', name: 'URL' },
	{ id: 'organisationAddress.city', name: 'City' },
	{ id: 'organisationAddress.state', name: 'State' },
	{ id: 'organisationAddress.country', name: 'Country' },
	{ id: 'organisationEmail', name: 'Email Address' },
	{ id: 'organisationPhone', name: 'Phone Number' },
];

const initialSortColumn: models.SortColumn = {
	path: 'organisationName',
	order: 'asc',
};

const columns: models.Column[] = [
	{
		path: 'organisationLogo',
		label: 'Logo',
		content: (o: models.Organization) => (
			<img src={utils.preventImageCaching(o.organisationLogo)} alt={o.organisationName} />
		),
	},
	{
		path: 'organisationName',
		label: 'Organization Name',
	},
	{
		path: 'organisationAddress.city',
		label: 'City',
	},
	{
		path: 'organisationAddress.state',
		label: 'State',
	},
	{
		path: 'organisationAddress.country',
		label: 'Country',
	},
	{
		path: 'isActive',
		label: 'Status',
		content: (user) => <Status isActive={user.isActive} />,
	},
	{
		path: '_id',
		label: '',
		content: (o: models.Organization) => (
			<button className={classNames.viewMore}>
				<Link to={`/dashboard/organizations/${o._id}`}>View</Link>
			</button>
		),
	},
];

const Facilities = withFacilities(Organizations);
export default withRouter(withOrganizations(Facilities));
